import React from "react";
import ProjectLogo from "../../Components/ProjectLogo/ProjectLogo";
import "./cells.css";

// Presentational building blocks for the dashboard tables. They only change
// how a value looks; every value shown is the same as before.

export const Empty = ({ children = "—" }) => <span className="cell-empty">{children}</span>;

// Coloured pill; falls back to muted text when there is no value.
export const Pill = ({ tone, children, empty }) =>
  children ? <span className={`pill pill--${tone}`}>{children}</span> : <Empty>{empty}</Empty>;

// Name with a coloured initials avatar.
export const PersonName = ({ name }) =>
  name ? (
    <span className="cell-person">
      <ProjectLogo name={name} size={28} className="is-round" />
      <span className="cell-person-name">{name}</span>
    </span>
  ) : (
    <Empty />
  );

// Visit count; repeat visitors (2+) are highlighted.
export const VisitCount = ({ count, fallback = "" }) => {
  const value = count || fallback;
  if (!value) return <Empty>{""}</Empty>;
  return (
    <span className={`visit-count${Number(value) > 1 ? " visit-count--repeat" : ""}`}>{value}</span>
  );
};

// Visit photos as an overlapping stack: the latest two, then "+N". Each
// thumbnail opens the full-size viewer (and doesn't select the row).
export const VisitPhotos = ({ urls = [], onOpen }) => {
  const ordered = urls.filter(Boolean).slice().reverse(); // latest first, as before
  if (ordered.length === 0) return <Empty />;
  const shown = ordered.slice(0, 2);
  const extra = ordered.length - shown.length;
  const open = (url) => (event) => {
    event.stopPropagation();
    onOpen(url);
  };
  return (
    <span className="visit-photos" aria-label={`${ordered.length} visit photo${ordered.length > 1 ? "s" : ""}`}>
      {shown.map((url, i) => (
        <button
          type="button"
          key={`${url}-${i}`}
          className="visit-photo"
          style={{ zIndex: shown.length - i + 1 }}
          onClick={open(url)}
          title={i === 0 ? "Latest visit photo" : "Earlier visit photo"}
        >
          <img src={url} alt={i === 0 ? "Latest visit" : "Earlier visit"} loading="lazy" />
        </button>
      ))}
      {extra > 0 && (
        <button
          type="button"
          className="visit-photo visit-photo--more"
          onClick={open(ordered[shown.length])}
          title={`${extra} more visit photo${extra > 1 ? "s" : ""}`}
        >
          +{extra}
        </button>
      )}
    </span>
  );
};

// Project name with its monogram logo.
export const ProjectName = ({ name }) =>
  name ? (
    <span className="cell-project">
      <ProjectLogo name={name} size={22} />
      {name}
    </span>
  ) : (
    <Empty />
  );
